import * as THREE from 'three'
// import { XR_PALM_CONTENT_OFFSET } from './xrPalmContentOrientation.js'

/** Middle-metacarpal vs fox +Y-up: flip vertical. */
const qPitchFlip = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI, 0, 0))

/**
 * Face palm toward viewer (was showing the model’s back). If head/body swap front/back,
 * negate Y (replace Math.PI → -Math.PI or 0).
 */
const qYawTowardViewer = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI, 0))

/**
 * Combines corrections; applied on XRRig after palm quaternion.
 */
const XR_PALM_CONTENT_OFFSET = new THREE.Quaternion().multiplyQuaternions(
    qPitchFlip,
    qYawTowardViewer
)

/** World uniform scale factor on tracking rig for fox / ground / plants */
const XR_RIG_SCENE_SCALE = 0.066

/**
 * When anchoring on a hand: offset the rig along the palm outward normal (toward fingers / open palm).
 * Metres — keeps placement coherent when you twist your wrist (orthogonal to palm plane).
 */
const XR_RIG_PALM_NORMAL_OFFSET = -0.033

/**
 * Extra world +Y on top of palm anchor — metres. The palm normal is usually mostly horizontal toward
 * the fingers; the old behavior was only world +Y lift, which this restores on top of the normal offset.
 */
const XR_RIG_PALM_WORLD_Y_OFFSET = 0.0

/** Lift entire XR content along world +Y after surface hit anchor (floor / hit-test — metres). */
const XR_RIG_HIT_WORLD_Y_OFFSET = 0.1

/**
 * Applies XR anchor position, palm orientation (+ offset), and scene scale once on a shared parent.
 * Web/desktop: rig stays identity — children behave as before relative to worldGroup.
 */
export default class XRRig {
    constructor(experience) {
        this.experience = experience
        this.group = experience.xrRig
        this._rigPalmOffsetDir = new THREE.Vector3()
    }

    update() {
        const renderer = this.experience.renderer.instance
        const { handTracking, hitTesting } = this.experience

        if (!renderer.xr.isPresenting) {
            this.resetIdentity()
            return
        }

        if (handTracking.isAnyHandTracked()) {
            const pos = handTracking.getPrimaryPalmPosition()
            const palmQuat = handTracking.getPrimaryPalmQuaternion()
            if (!pos || !palmQuat) {
                this.resetIdentity()
                return
            }
            this.group.position.copy(pos)
            if (handTracking.getPrimaryPalmOutwardDirection(this._rigPalmOffsetDir)) {
                this.group.position.addScaledVector(this._rigPalmOffsetDir, XR_RIG_PALM_NORMAL_OFFSET)
            }
            this.group.position.y += XR_RIG_PALM_WORLD_Y_OFFSET
            this.group.quaternion.copy(palmQuat).multiply(XR_PALM_CONTENT_OFFSET)
            // const pinch = handTracking.getScale()
            this.group.scale.setScalar(XR_RIG_SCENE_SCALE) // * pinch
            return
        }

        if (hitTesting.hasHit()) {
            const pos = hitTesting.getHitPosition()
            if (!pos) {
                this.resetIdentity()
                return
            }
            this.group.position.copy(pos)
            this.group.position.y += XR_RIG_HIT_WORLD_Y_OFFSET
            this.group.quaternion.identity()
            this.group.scale.setScalar(XR_RIG_SCENE_SCALE)
            return
        }

        this.resetIdentity()
    }

    resetIdentity() {
        this.group.position.set(0, 0, 0)
        this.group.quaternion.identity()
        this.group.scale.set(1, 1, 1)
    }
}
